import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import DifficultySelector, { GradeRange, getAvailableGrades, getDifficultyNumbers } from "@/components/DifficultySelector";
import { ArrowLeft, FileText, Download, RefreshCw } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import MathText from "@/components/MathText";
import type { Topic, DifficultyLevel } from "@shared/schema";
import { selectBalancedItems } from "@/lib/questionSelection";
import { getRequestAuthHeaders } from "@/lib/requestAuth";
import { useProfile } from "@/hooks/use-profile";
import ModuleLockedState from "@/components/ModuleLockedState";
import { useModuleStatus } from "@/hooks/use-module-status";
import { useRolePerspective } from "@/hooks/use-role-perspective";
import { useSubject } from "@/contexts/SubjectContext";
import { getYearCategory, CATEGORY_ORDER, isSubjectMatch, formatYearGroup } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
interface GeneratedQuestion {
  topicName: string;
  question: string;
  answer: string;
  explanation: string;
  difficulty: DifficultyLevel;
}

export default function TeacherWorksheet() {
  const [, setLocation] = useLocation();
  const { profile } = useProfile();
  const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [questionCount, setQuestionCount] = useState<number>(10);
  const [difficulty, setDifficulty] = useState<GradeRange[]>(['1-4', '5-6', '7-8', '9']);
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);
  const [isGenerated, setIsGenerated] = useState(false);
  const [showAnswers, setShowAnswers] = useState(true);
  const [showOnlyMyTopics, setShowOnlyMyTopics] = useState(false);

  const { status: moduleStatus, loading: moduleStatusLoading } = useModuleStatus('worksheet');
  const { currentSubject } = useSubject();

  // Load topics from API
  useEffect(() => {
    if (!currentSubject) return;

    const loadTopics = async () => {
      setLoading(true);
      try {
        const authHeaders = await getRequestAuthHeaders();
        const response = await fetch(`/api/topics?mode=worksheet&subject=${currentSubject.id}`, { headers: authHeaders });
        if (response.ok) {
          const data = await response.json();
          // Show both admin and teacher custom topics
          const worksheetTopics = data.filter((t: Topic) => t.mode === 'worksheet' && isSubjectMatch(t, currentSubject.id));
          setTopics(worksheetTopics);
        }
      } catch (error) {
        console.error('Failed to load topics:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTopics();
  }, [currentSubject]);

  const isTeacher = effectiveRole === 'teacher';
  const visibleTopics = isTeacher && showOnlyMyTopics ? topics.filter(t => !t.is_core) : topics;

  const toggleTopic = (topicId: string) => {
    setSelectedTopics(prev =>
      prev.includes(topicId)
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  };

  // Calculate maximum available questions based on selection and difficulty
  const maxAvailableQuestions = Math.max(1, selectedTopics.length > 0 
    ? visibleTopics.filter(t => selectedTopics.includes(t.id))
            .reduce((sum, t) => {
              const diffNumbers = getDifficultyNumbers(difficulty);
              const suitableQ = t.questions.filter(q => diffNumbers.includes(q.difficulty)).length;
              return sum + suitableQ;
            }, 0)
    : 50);

  // Auto-adjust question count if it exceeds available
  useEffect(() => {
    if (selectedTopics.length > 0 && questionCount > maxAvailableQuestions) {
      setQuestionCount(maxAvailableQuestions);
    }
  }, [maxAvailableQuestions, questionCount, selectedTopics.length]);

  const handleGenerateWorksheet = () => {
    if (selectedTopics.length === 0) return;

    const topicPools = selectedTopics
      .map((topicId) => {
        const topic = visibleTopics.find((candidate) => candidate.id === topicId);
        if (!topic) return [];

        const diffNumbers = getDifficultyNumbers(difficulty);
        return topic.questions
          .filter((question) => diffNumbers.includes(question.difficulty))
          .map((question) => ({
            topicName: topic.name,
            question: question.question,
            answer: question.options[question.correctAnswer],
            explanation: question.explanation,
            difficulty: question.difficulty,
          }));
      })
      .filter((pool) => pool.length > 0);

    const selected = selectBalancedItems(topicPools, questionCount);

    setGeneratedQuestions(selected);
    setIsGenerated(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    const headers = ['#', 'Topic', 'Question', 'Answer', 'Explanation'];
    const rows = generatedQuestions.map((q, index) => [
      index + 1,
      q.topicName,
      q.question,
      q.answer,
      q.explanation
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `worksheet-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const groupedTopics = visibleTopics.reduce((acc: Record<string, Record<string, Record<string, Topic[]>>>, topic: Topic) => {
      const cat = getYearCategory(topic.yearLevel);
      if (!acc[cat]) acc[cat] = {};
      const yearStr = formatYearGroup(topic.yearLevel);
      if (!acc[cat][yearStr]) acc[cat][yearStr] = {};
      const subCat = topic.category || "Uncategorized";
      if (!acc[cat][yearStr][subCat]) acc[cat][yearStr][subCat] = [];
      acc[cat][yearStr][subCat].push(topic);
      return acc;
  }, {});

  if (!moduleStatusLoading && moduleStatus && moduleStatus !== 'active' && effectiveRole !== 'admin') {
    return <ModuleLockedState status={moduleStatus} />;
  }

  if (isGenerated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-5xl mx-auto space-y-6">
            {/* Header - hidden when printing */}
            <div className="flex items-center justify-between print:hidden">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsGenerated(false)}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <h1 className="text-3xl font-bold font-serif">Generated Worksheet</h1>
                  <p className="text-muted-foreground mt-1">
                    {generatedQuestions.length} questions • Grades {difficulty.join(", ")}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <div className="flex items-center gap-2 mr-4">
                  <Switch
                    id="show-answers"
                    checked={showAnswers}
                    onCheckedChange={setShowAnswers}
                  />
                  <Label htmlFor="show-answers" className="cursor-pointer">
                    Show Answers
                  </Label>
                </div>
                <Button variant="outline" onClick={handleExportCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <Button onClick={handlePrint}>
                  <FileText className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>

            {/* Printable worksheet */}
            <div className="space-y-8">
              {/* Print header */}
              <div className="hidden print:block text-center mb-8">
                <h1 className="text-3xl font-bold">Addvance Maths Worksheet</h1>
                <p className="text-muted-foreground mt-2">
                  Grades {difficulty.join(", ")} • {generatedQuestions.length} Questions
                </p>
                <Separator className="my-4" />
              </div>

              {generatedQuestions.map((q, index) => (
                <Card key={index} className="p-6 space-y-4 page-break-inside-avoid">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 space-y-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            {q.topicName}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold">
                          <MathText>{q.question}</MathText>
                        </h3>
                      </div>

                      {showAnswers && (
                        <>
                          <Separator />

                          <div className="space-y-3 bg-muted/30 p-4 rounded-lg">
                            <div>
                              <p className="text-sm font-semibold text-primary mb-1">Answer:</p>
                              <p className="text-base font-medium"><MathText>{q.answer}</MathText></p>
                            </div>

                            <div>
                              <p className="text-sm font-semibold text-muted-foreground mb-1">Explanation:</p>
                              <p className="text-sm text-muted-foreground leading-relaxed"><MathText>{q.explanation}</MathText></p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>

        <style>{`
          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .print\\:hidden { display: none !important; }
            .print\\:block { display: block !important; }
            .page-break-inside-avoid { page-break-inside: avoid; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation("/teacher-dashboard")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold font-serif">Worksheet Generator</h1>
                <p className="text-muted-foreground mt-1">
                  Create printable worksheets with multiple questions
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Worksheet Settings</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="question-count" className="text-sm font-medium mb-2 block">
                    Number of Questions
                  </Label>
                  <Input
                    id="question-count"
                    type="number"
                    min={1}
                    max={maxAvailableQuestions}
                    value={questionCount || ""}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (isNaN(val)) {
                        setQuestionCount(0 as any);
                      } else {
                        setQuestionCount(val);
                      }
                    }}
                    onBlur={() => {
                      setQuestionCount(Math.max(1, Math.min(maxAvailableQuestions, questionCount || 1)));
                    }}
                    className="max-w-xs"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Choose up to {maxAvailableQuestions} valid questions
                  </p>
                </div>
                {isTeacher && (
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Switch
                      id="show-only-my-topics"
                      checked={showOnlyMyTopics}
                      onCheckedChange={setShowOnlyMyTopics}
                    />
                    <Label htmlFor="show-only-my-topics" className="cursor-pointer">
                      Show only my custom uploaded questions
                    </Label>
                  </div>
                )}
              </div>
            </Card>

            <DifficultySelector
                  selected={difficulty}
                  availableGrades={getAvailableGrades(visibleTopics.filter(t => selectedTopics.includes(t.id)).flatMap(t => t.questions))}
                  onChange={setDifficulty}
                />
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">
              Select Topics ({selectedTopics.length} selected)
            </h3>
            <div className="space-y-6">
              {loading ? (
                  <div className="flex justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
              ) : (
                <Accordion type="multiple" className="w-full space-y-4">
                  {CATEGORY_ORDER.filter(cat => groupedTopics[cat]).map(category => (
                    <AccordionItem key={category} value={category} className="border bg-card rounded-lg px-4 shadow-sm">
                      <AccordionTrigger className="hover:no-underline font-serif text-xl py-4">
                        {category}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-8 pt-2 pb-4">
                          {Object.entries(groupedTopics[category])
                            .sort(([yearA], [yearB]) => yearA.localeCompare(yearB, undefined, {numeric: true}))
                            .map(([yearStr, subGroups]) => (
                            <div key={yearStr} className="space-y-4">
                              <h3 className="text-lg font-bold border-b pb-2">{yearStr}</h3>
                              <div className="space-y-6">
                                {Object.entries(subGroups)
                                  .sort(([catA], [catB]) => catA.localeCompare(catB))
                                  .map(([catName, topics]) => (
                                  <div key={catName}>
                                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">{catName}</h4>
                                    <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                                      {topics.map((topic) => (
                                        <Card
                                          key={topic.id}
                                          className={`p-4 cursor-pointer transition-all ${selectedTopics.includes(topic.id)
                                            ? 'border-2 border-primary bg-primary/5'
                                            : 'hover:border-primary/50'
                                            }`}
                                          onClick={() => toggleTopic(topic.id)}
                                        >
                                          <div className="flex items-center gap-3">
                                            <Checkbox
                                              className="pointer-events-none"
                                              checked={selectedTopics.includes(topic.id)}
                                            />
                                            <div>
                                              <p className="font-medium">{topic.name}</p>
                                              <p className="text-xs text-muted-foreground">
                                                {topic.questions.length} questions
                                              </p>
                                            </div>
                                          </div>
                                        </Card>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button
              size="lg"
              onClick={handleGenerateWorksheet}
              disabled={selectedTopics.length === 0}
              className="w-full md:w-auto"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate Worksheet
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}





